/**
 * Backups on the page (decision 72; part 24e log, tables P, T, M, N, O, principles 4–6): when a run is due, which folder
 * it writes to and whether the browser still lets it, the reminder where there is no folder, and what the writer is
 * told. The bundles are made in a backup worker, one job each; this side reads the log, holds one timer and posts one
 * message a run (principle 5). Every effect (the log, the worker, the lock, the picker, the share sheet, the clock) is
 * injected, so the logic runs in tests. Part 41b: the backup lock (set, changed, turned off, checked; BP2, BP7, BP8,
 * E15–E17), its key derived in the KDF worker, never on the page.
 */
import { toHex } from '@cockatrice/core';
import { sameCheckValue } from '@cockatrice/crypto';
import type { Locks } from '@cockatrice/storage';
import { createSignal } from 'solid-js';
import type { Accessor } from 'solid-js';
import { localDay } from '../count/tally.ts';
import type { BackupKdf } from '../kdf/host.ts';
import type { BackupLog } from './db.ts';
import { isDirectory } from './folder.ts';
import type { DirectoryLike } from './folder.ts';
import { checkDue, LOCKED_ENDING, nextDay } from './lock.ts';
import type { BackupLock, LockState } from './lock.ts';
import { daysSince, newBookBackups, NO_META } from './log.ts';
import type { BackupMeta, BackupWay, BookBackups } from './log.ts';
import { readReply } from './protocol.ts';
import type { BackupJob, BackupReply, ReadyBundle } from './protocol.ts';

/** T1: how long after the workspace is ready the first check waits (cold start untouched, decision 14). */
export const START_MS = 30_000;

/** T2: how often a visible page checks whether a run is due. */
export const EVERY_MS = 3_600_000;

/** M7: how long a downloaded reminder's file stays reachable by its URL, for the download its link started to read it. */
export const KEEP_URL_MS = 60_000;

/** M12 (deep-fix r2): how old the reminder's file may grow before a check makes it again. */
export const REFRESH_MS = 15 * 60_000;

/** T9: the Web Lock one run at a time holds, across the origin's tabs. */
export const BACKUP_LOCK = 'cockatrice-backup';

/** Table P: the folder: none possible here (P4–P7), none chosen (P3, N7), or the one chosen, by its name. */
export type FolderState = { readonly kind: 'unsupported' } | { readonly kind: 'none' } | { readonly kind: 'set'; readonly name: string };

/** Table N: what the writer is told about the folder backups (N1–N7), or that this build is outdated. */
export type BackupNotice =
  | { readonly kind: 'paused' | 'refused' | 'gone' | 'full' | 'failed' | 'lost' | 'outdated' }
  | { readonly kind: 'books'; readonly books: readonly string[] };

/** M6: the reminder: a book's bundle ready to save, its object URL, and how many other books are due. */
export interface Reminder extends ReadyBundle {
  readonly url: string;
  readonly others: number;
}

/** Part 41b, E1, E2, E7: the backup lock as the writer is shown it: off, on, or stored but unreadable (set it again). */
export type LockShown = 'off' | 'on' | 'unreadable';

/** Part 41b: what setting and checking the backup lock takes: the KDF worker's backup key, and randomness for its ID. */
export interface LockKeys {
  /** BP2: `backupKdf(…).backupKey` (`kdf/host.ts`): Argon2id in a worker of its own. */
  readonly backupKey: BackupKdf['backupKey'];
  /** `crypto.getRandomValues`, filling `bytes`: each lock's ID (E4–E6). */
  readonly random: (bytes: Uint8Array<ArrayBuffer>) => void;
}

/** A backup worker, as far as the controller drives it (a `Worker`). */
export interface BackupWorker {
  addEventListener(type: 'message' | 'messageerror' | 'error', listener: (event: unknown) => void): void;
  postMessage(message: BackupJob): void;
  terminate(): void;
}

/** What the controller needs; everything with an effect is injected. */
export interface BackupOptions {
  /** Opens the backup log (`openBackupLog`); `gone` is called when it closed itself for a later build (r1-2). */
  readonly openLog: (gone: () => void) => Promise<BackupLog>;
  /** Starts a backup worker. */
  readonly worker: () => BackupWorker;
  /** The origin's Web Locks; undefined without them. */
  readonly locks: Locks | undefined;
  /** P1–P3: the browser's folder picker (`showDirectoryPicker`, read and write), or null where there is none (P4–P7). */
  readonly pickFolder: (() => Promise<unknown>) | null;
  /** M7: the share sheet with a file (`navigator.share`), or null where files can't be shared. */
  readonly share: ((file: File) => Promise<void>) | null;
  /** This device's ID, null before the workspace is ready. */
  readonly device: () => string | null;
  /** M6: the open book, offered first. */
  readonly openBook: () => string | null;
  /** The untitled book's title. */
  readonly untitled: () => string;
  /** Milliseconds since the Unix epoch, and minutes ahead of UTC at a time. */
  readonly now: () => number;
  readonly zone: (at: number) => number;
  /** Runs `run` after `ms`; returns what cancels it. */
  readonly schedule: (ms: number, run: () => void) => () => void;
  /** T4: whether the page is hidden. */
  readonly hidden: () => boolean;
  /** An object URL for a file, and what lets it go. */
  readonly objectUrl: (file: Blob) => string;
  readonly revokeUrl: (url: string) => void;
  /** Part 41b: the backup lock's key and ID; absent (tests that don't reach it), setting and checking it fail. */
  readonly lockKeys?: LockKeys;
}

/** A message event's data; undefined for anything else. */
function dataOf(event: unknown): unknown {
  return typeof event === 'object' && event !== null ? Reflect.get(event, 'data') : undefined;
}

/** Table N: the notice a run's reply leaves (null: nothing to say). */
export function noticeOf(reply: BackupReply | null): BackupNotice | null {
  if (reply === null) return { kind: 'failed' };
  switch (reply.kind) {
    case 'ran':
    case 'checked':
      return reply.failed.length > 0 ? { kind: 'books', books: reply.failed } : null;
    case 'stopped':
      // N2–N5; a book the run had already failed is said once the folder works again.
      return { kind: reply.reason === 'permission' ? 'refused' : reply.reason };
    case 'failed':
      return { kind: reply.reason };
    case 'lock-unreadable':
      // E7: said by the lock's own state (`lock()` is `unreadable`), which asks for it to be set again.
      return null;
  }
}

/** Whether the lock stored is still the one of ID `only` (null: whatever is stored, or none). */
async function stillStored(log: BackupLog, only: string | null): Promise<boolean> {
  if (only === null) return true;
  const stored = await log.readLock();
  return stored.kind === 'on' && stored.lock.id === only;
}

/** The backups' state and their one worker at a time. */
export class BackupController {
  readonly folder: Accessor<FolderState>;
  readonly notice: Accessor<BackupNotice | null>;
  readonly reminder: Accessor<Reminder | null>;
  /** Whether a run or a check is under way in this tab (T10). */
  readonly running: Accessor<boolean>;
  /** Each book's backups, as last read (ages in settings and the book list). */
  readonly records: Accessor<ReadonlyMap<string, BookBackups>>;
  /** Part 41b, E1, E2, E7: the backup lock, as last read. */
  readonly lock: Accessor<LockShown>;
  /** Part 41b, BP7, E15–E17: whether the passphrase check is due, as of the last read. */
  readonly lockCheckDue: Accessor<boolean>;
  /** Part 41b, E3: the books whose locked file didn't open back in the last run or check, so nothing was written for them. */
  readonly unlocked: Accessor<readonly string[]>;
  readonly #setLock: (lock: LockShown) => void;
  readonly #setLockCheckDue: (due: boolean) => void;
  readonly #setUnlocked: (books: readonly string[]) => void;
  /** The lock as last read (null: off or unreadable). */
  #lock: BackupLock | null = null;
  /** Deep-fix r1, C1, C3: a reminder was let go for a lock changed, so the next check makes it again. */
  #remake = false;
  readonly #setFolder: (state: FolderState) => void;
  readonly #setNotice: (notice: BackupNotice | null) => void;
  readonly #setReminder: (reminder: Reminder | null) => void;
  readonly #setRunning: (running: boolean) => void;
  readonly #setRecords: (records: ReadonlyMap<string, BookBackups>) => void;
  readonly #options: BackupOptions;
  #log: Promise<BackupLog> | null = null;
  #handle: DirectoryLike | null = null;
  #cancel: (() => void) | null = null;
  #started = false;
  /** Set by {@link stop}: no worker starts after. */
  #stopped = false;
  /** Ends the job under way (its worker ended, its reply given), or null with none. */
  #endWork: ((reply: BackupReply | null) => void) | null = null;
  /** T10′: this tab's jobs under way or waiting, and the end of the last one, which the next waits for. */
  #jobs = 0;
  #queue: Promise<unknown> = Promise.resolve();

  constructor(options: BackupOptions) {
    this.#options = options;
    [this.folder, this.#setFolder] = createSignal<FolderState>({ kind: options.pickFolder === null ? 'unsupported' : 'none' });
    [this.notice, this.#setNotice] = createSignal<BackupNotice | null>(null);
    [this.reminder, this.#setReminder] = createSignal<Reminder | null>(null);
    [this.running, this.#setRunning] = createSignal(false);
    [this.records, this.#setRecords] = createSignal<ReadonlyMap<string, BookBackups>>(new Map());
    [this.lock, this.#setLock] = createSignal<LockShown>('off');
    [this.lockCheckDue, this.#setLockCheckDue] = createSignal(false);
    [this.unlocked, this.#setUnlocked] = createSignal<readonly string[]>([]);
  }

  /**
   * BP2, BP8, E6: sets the backup passphrase, or changes it: a key derived in the KDF worker under a new KDF record (a
   * fresh salt), a new random ID, set and checked now. Files already written keep the passphrase they were made with.
   * `failed` when the key can't be derived or the lock can't be kept.
   */
  async setLock(passphrase: string): Promise<'set' | 'failed'> {
    const keys = this.#options.lockKeys;
    if (keys === undefined) return 'failed';
    const derived = await keys.backupKey(passphrase, null);
    if (derived.kind === 'failed') return 'failed';
    const bytes = new Uint8Array(16);
    keys.random(bytes);
    const id = toHex(bytes);
    const now = this.#options.now();
    return (await this.#changeLock({ id, key: derived.key, check: derived.check, setAt: now, checkedAt: now, later: null }, null)) ? 'set' : 'failed';
  }

  /** BP8, E5: turns the backup lock off: new backups are plaintext; files already written stay locked. */
  async turnOffLock(): Promise<'off' | 'failed'> {
    return (await this.#changeLock(null, null)) ? 'off' : 'failed';
  }

  /**
   * BP7, E15, E16: checks `passphrase` against the lock set: its key derived under the lock's own KDF record and its check
   * value compared. Right: checked now, nothing put off (the next check {@link CHECK_DAYS} on). `failed` with no lock, a
   * derivation that fails, or a lock changed meanwhile (another tab).
   */
  async checkLock(passphrase: string): Promise<'right' | 'wrong' | 'failed'> {
    const keys = this.#options.lockKeys;
    await this.#withLog((log) => this.#readLock(log));
    const lock = this.#lock;
    if (keys === undefined || lock === null) return 'failed';
    const derived = await keys.backupKey(passphrase, lock.key.kdf);
    if (derived.kind === 'failed') return 'failed';
    if (!sameCheckValue(derived.check, lock.check)) return 'wrong';
    return (await this.#changeLock({ ...lock, checkedAt: this.#options.now(), later: null }, lock.id)) ? 'right' : 'failed';
  }

  /** E17: **Remind me later**: the check put off until tomorrow's local day (2.2.3). */
  checkLockLater(): void {
    const lock = this.#lock;
    this.#setLockCheckDue(false);
    if (lock === null) return;
    const now = this.#options.now();
    void this.#changeLock({ ...lock, later: nextDay(now, this.#options.zone(now)) }, lock.id);
  }

  /**
   * Stores `lock` (null: turned off); with `only`, only while the lock stored is still that one (a check's or a put-off's
   * outcome never brings back a lock another tab changed or turned off since). A reminder's file made under the old state
   * is let go, so none is saved locked or not against the writer's latest choice. True once kept; the state read again.
   */
  async #changeLock(lock: BackupLock | null, only: string | null): Promise<boolean> {
    let kept = false;
    await this.#withLog(async (log) => {
      kept = await stillStored(log, only).then(async (still) => {
        // Deep-fix r1, C1, C5: a reminder's file is let go by the read below only when its lock is no longer the one stored.
        if (still) await log.writeLock(lock);
        return still;
      }).catch(() => false);
      await this.#readLock(log);
    });
    return kept;
  }

  /** E1, E2, E7, BP7: the lock as the log holds it now, and whether its check is due; a read that fails leaves them. */
  async #readLock(log: BackupLog): Promise<void> {
    let state: LockState;
    try {
      state = await log.readLock();
    } catch {
      // Deep-fix r3, E7: a lock that can't be read at all is unreadable, as the worker takes it: never shown as off.
      state = { kind: 'unreadable' };
    }
    this.#lock = state.kind === 'on' ? state.lock : null;
    this.#setLock(state.kind);
    // Deep-fix r1, C3, C6: a reminder's file made under a lock no longer stored (changed in another tab, or unreadable)
    // is let go, so none is saved against the writer's latest choice.
    const reminder = this.reminder();
    if (reminder !== null && (state.kind === 'unreadable' || reminder.lock !== (this.#lock?.id ?? null))) {
      this.#forgetReminder();
      this.#remake = true;
    }
    const now = this.#options.now();
    this.#setLockCheckDue(this.#lock !== null && checkDue(this.#lock, now, this.#options.zone(now)));
  }

  /** T1, T2: reads the folder and the log, and checks {@link START_MS} later, then every {@link EVERY_MS}; once. */
  start(): void {
    if (this.#started) return;
    this.#started = true;
    void this.#load();
    this.#every(START_MS);
  }

  /** The page closing: no timer, no worker; the log let go. */
  stop(): void {
    this.#stopped = true;
    this.#cancel?.();
    this.#cancel = null;
    // The job under way ends with no reply, so nothing waits on a worker that will never answer.
    this.#endWork?.(null);
    this.#forgetReminder();
    const log = this.#log;
    this.#log = null;
    void log?.then((open) => {
      open.close();
    }, () => undefined);
  }

  /** T3: the page shown again: a check, if one is due. */
  visible(): void {
    if (this.#started) void this.check();
  }

  /** T1–T4, T9, T10: runs or checks if today's hasn't happened yet; a hidden page or a run under way starts nothing. */
  async check(): Promise<void> {
    if (this.running() || this.#options.hidden()) return;
    await this.#withLog(async (log) => {
      const meta = await this.#meta(log);
      await this.#refresh(log);
      await this.#reloadFolder(log, false);
      if (meta.ran !== this.#today()) await this.#due(log, false);
      // M12: a reminder's file older than REFRESH_MS is made again, so a press saves the book as it is about now.
      // Deep-fix r1, C1, C3: one let go for the lock changed is made again under the lock stored.
      else if (this.#stale() || this.#remake) await this.#runCheck(log, this.#handle !== null, false);
    });
  }

  /** M12: whether the reminder's file is older than {@link REFRESH_MS}. */
  #stale(): boolean {
    const reminder = this.reminder();
    return reminder !== null && this.#options.now() - reminder.at >= REFRESH_MS;
  }

  /**
   * T5: **Back up now**: a run whatever ran today (the folder's, or with none the reminder's check). r1-1: the press asks
   * the browser for the folder first, with its own activation, so a permission asked again (P2) is one press here too.
   */
  async backUpNow(): Promise<void> {
    // T10′: a press while a job runs waits for it (deep-fix r3).
    await this.#handle?.requestPermission({ mode: 'readwrite' }).catch(() => 'denied');
    await this.#withLog(async (log) => {
      // O2′: the folder as stored now, which another tab may have changed.
      await this.#reloadFolder(log, false);
      await this.#due(log, true);
    });
  }

  /** T6, P3, N2, N3, N7: the picker; the folder chosen is kept and run at once. A picker closed changes nothing. */
  async choose(): Promise<void> {
    const pick = this.#options.pickFolder;
    if (pick === null) return;
    let handle: unknown;
    try {
      handle = await pick();
    } catch {
      // The picker closed (`AbortError`) or refused: the folder stays as it was.
      return;
    }
    if (!isDirectory(handle)) return;
    await this.#withLog(async (log) => {
      try {
        await log.setFolder(handle);
      } catch {
        this.#setNotice({ kind: 'failed' });
        return;
      }
      this.#handle = handle;
      this.#setFolder({ kind: 'set', name: handle.name });
      this.#setNotice(null);
      this.#forgetReminder();
      await this.#runFolder(log, handle, true);
    });
  }

  /** N1, T7: asks the browser again (this press's activation); granted: the run at once; refused: N2. */
  async allow(): Promise<void> {
    const handle = this.#handle;
    if (handle === null) return;
    let state: PermissionState;
    try {
      state = await handle.requestPermission({ mode: 'readwrite' });
    } catch {
      state = 'denied';
    }
    if (state !== 'granted') {
      this.#setNotice({ kind: 'refused' });
      return;
    }
    this.#setNotice(null);
    await this.#withLog(async (log) => {
      // O2′c (deep-fix r5): the folder as stored now; run only if it is still the one asked about, else as due.
      await this.#reloadFolder(log, false);
      const now = this.#handle;
      if (now === null) return;
      if (await now.isSameEntry(handle).catch(() => false)) await this.#runFolder(log, now, true);
      else await this.#due(log, true);
    });
  }

  /** **Stop using this folder**: forgets the folder; nothing in it is touched (principle 2). */
  async forget(): Promise<void> {
    await this.#withLog(async (log) => {
      try {
        await log.setFolder(null);
      } catch {
        this.#setNotice({ kind: 'failed' });
        return;
      }
      this.#handle = null;
      this.#setFolder({ kind: this.#options.pickFolder === null ? 'unsupported' : 'none' });
      this.#setNotice(null);
    });
  }

  /** M8, table N: puts the notice and the reminder away until tomorrow (2.2.4). */
  async later(): Promise<void> {
    this.#setNotice(null);
    this.#forgetReminder();
    const today = this.#today();
    await this.#withLog((log) => log.changeMeta((meta) => ({ ...meta, later: today })).catch(() => undefined));
  }

  /** Principle 8: whole local days since book `bookId`'s last backup, or null for none. */
  age(bookId: string): number | null {
    const last = this.records().get(bookId)?.last;
    if (last === undefined || last === null) return null;
    const now = this.#options.now();
    return daysSince(last.at, now, this.#options.zone(now));
  }

  /** M7: whether the reminder's file can be shared here (else it is downloaded). */
  canShare(): boolean {
    return this.#options.share !== null;
  }

  /** M7: shares the reminder's file; recorded once shared, the next due book made ready (M10); a cancel records nothing. */
  async shareReminder(): Promise<void> {
    const reminder = this.reminder();
    const share = this.#options.share;
    if (reminder === null || share === null) return;
    try {
      // E8: a locked file is no zip.
      await share(new File([reminder.file], reminder.name, { type: reminder.name.endsWith(LOCKED_ENDING) ? 'application/octet-stream' : 'application/zip' }));
    } catch {
      // M7: cancelled (`AbortError`) or refused: the reminder stays.
      return;
    }
    this.#forgetReminder();
    await this.#saved(reminder, 'download');
  }

  /**
   * M7: the reminder's file downloaded (its link pressed): recorded, the next due book made ready (M10). The link's own
   * download starts once its press is over, so the reminder goes a task later, and its file's URL after
   * {@link KEEP_URL_MS}.
   */
  async downloaded(): Promise<void> {
    const reminder = this.reminder();
    if (reminder === null) return;
    await new Promise<void>((resolve) => {
      this.#options.schedule(0, resolve);
    });
    // Unless Later or a new reminder took it away meanwhile.
    if (this.reminder() === reminder) this.#setReminder(null);
    this.#options.schedule(KEEP_URL_MS, () => {
      this.#options.revokeUrl(reminder.url);
    });
    await this.#saved(reminder, 'download');
  }

  /** T8, M9: a bundle of book `bookId` exported and saved by hand, made at `at` with digest `sha256`. */
  async exported(bookId: string, sha256: string, at: number): Promise<void> {
    await this.#withLog(async (log) => {
      await this.#record(log, bookId, sha256, at, 'export', null);
      if (this.reminder()?.bookId === bookId) this.#forgetReminder();
      await this.#refresh(log);
    });
  }

  /** M7, M10: a reminder's file saved: recorded, and the next due book looked for. */
  async #saved(reminder: Reminder, way: BackupWay): Promise<void> {
    await this.#withLog(async (log) => {
      await this.#record(log, reminder.bookId, reminder.sha256, reminder.at, way, reminder.lock);
      await this.#refresh(log);
      // M10; r4-2: a folder's notice standing (M11) stays unless the check has one of its own; O2′g: the folder as stored.
      await this.#reloadFolder(log, false);
      if (reminder.others > 0) await this.#runCheck(log, this.#handle !== null, true);
    });
  }

  /** O3: the book's last backup set in one transaction; D: under `lock` (its ID; null for a plain one). */
  async #record(log: BackupLog, bookId: string, sha256: string, at: number, way: BackupWay, lock: string | null): Promise<void> {
    try {
      await log.update(
        bookId,
        () => newBookBackups(bookId, at),
        (record) => ({ ...record, last: { at, sha256, way, lock } }),
      );
    } catch {
      this.#setNotice({ kind: 'failed' });
    }
  }

  /** Table P, N1, N2, N7: the folder's run when it can write, its notice when it can't, else the reminder's check. */
  async #due(log: BackupLog, pressed: boolean): Promise<void> {
    const handle = this.#handle;
    if (handle !== null) {
      let state: PermissionState;
      try {
        state = await handle.queryPermission({ mode: 'readwrite' });
      } catch {
        state = 'denied';
      }
      if (state === 'granted') {
        await this.#runFolder(log, handle, pressed);
        return;
      }
      // N1, N2: never a silent stop (principle 4), unless put off today (M8); M11: and the reminder as without a folder.
      if (pressed || (await this.#meta(log)).later !== this.#today()) this.#setNotice({ kind: state === 'prompt' ? 'paused' : 'refused' });
      await this.#runCheck(log, true, pressed);
      return;
    }
    await this.#runCheck(log, false, pressed);
  }

  /** T5–T7, T9: a folder run under the lock (waited for when pressed); its notice; today marked as run. */
  async #runFolder(log: BackupLog, handle: DirectoryLike, pressed: boolean): Promise<void> {
    const device = this.#options.device();
    if (device === null) return;
    const reply = await this.#job({ kind: 'run', folder: handle, device, untitled: this.#options.untitled() }, pressed);
    if (reply === undefined) return;
    this.#setNotice(noticeOf(reply));
    // E3: the books whose locked file didn't open back in this run.
    if (reply !== null && (reply.kind === 'ran' || reply.kind === 'stopped')) this.#setUnlocked(reply.unlocked);
    await this.#ran(log);
    // M11: a run stopped on the folder (N2–N5) leaves the reminder to come as without one.
    if (reply?.kind === 'stopped') await this.#runCheck(log, true, pressed);
  }

  /**
   * M5, M6: a reminder check under the lock (waited for when `pressed`); the first due book's file made ready. With
   * `keep` (M11) the folder's notice standing is kept unless the check has one of its own.
   */
  async #runCheck(log: BackupLog, keep: boolean, pressed: boolean): Promise<void> {
    const device = this.#options.device();
    if (device === null) return;
    this.#remake = false;
    const reply = await this.#job({ kind: 'check', device, untitled: this.#options.untitled(), prefer: this.#options.openBook() }, pressed);
    if (reply === undefined) return;
    const notice = noticeOf(reply);
    if (!keep || notice !== null) this.#setNotice(notice);
    const later = (await this.#meta(log)).later === this.#today();
    // M6, M12: the reminder follows the check: the first due book's file, or none when no book is due any more.
    if (reply !== null && reply.kind === 'checked') {
      // E3, E8: a check's books not locked; after a folder's run (M11, `keep`) added to the run's.
      this.#setUnlocked(keep ? [...new Set([...this.unlocked(), ...reply.unlocked])] : reply.unlocked);
      this.#forgetReminder();
      // Deep-fix r1, C2, C4: the lock read again, so a file made under one changed while the check ran is never offered.
      await this.#readLock(log);
      const ready = reply.ready !== null && this.lock() !== 'unreadable' && reply.ready.lock === (this.#lock?.id ?? null) ? reply.ready : null;
      if (ready !== null && !later) this.#setReminder({ ...ready, url: this.#options.objectUrl(ready.file), others: reply.due.length - 1 });
    }
    await this.#ran(log);
  }

  /** T: today marked as run, and the records read again. */
  async #ran(log: BackupLog): Promise<void> {
    const today = this.#today();
    await log.changeMeta((meta) => ({ ...meta, ran: today })).catch(() => undefined);
    await this.#refresh(log);
  }

  /**
   * T9, T10: `job` in a worker under the Web Lock (waited for when `wait`, else skipped while another tab holds it);
   * its reply (null for a worker that failed or answered nothing readable), or undefined when it didn't run.
   */
  async #job(job: BackupJob, wait: boolean): Promise<BackupReply | null | undefined> {
    // T10′ (deep-fix r3): a timer's job is skipped while this tab runs one; a press's waits for it, then runs.
    if (this.#jobs > 0 && !wait) return undefined;
    this.#jobs += 1;
    this.#setRunning(true);
    const turn = this.#queue.then(() => this.#locked(job, wait));
    this.#queue = turn;
    try {
      return await turn;
    } finally {
      this.#jobs -= 1;
      this.#setRunning(this.#jobs > 0);
    }
  }

  /** T9: `job` under the Web Lock; its reply, null for a failure, undefined when another tab held the lock. */
  async #locked(job: BackupJob, wait: boolean): Promise<BackupReply | null | undefined> {
    try {
      const locks = this.#options.locks;
      if (locks === undefined) return await this.#work(job);
      let reply: BackupReply | null | undefined;
      await locks.request(BACKUP_LOCK, { ifAvailable: !wait }, async (lock) => {
        if (lock !== null) reply = await this.#work(job);
      });
      return reply;
    } catch {
      return null;
    }
  }

  /** One worker for `job`, ended with its reply (null for an error or an unreadable message). */
  #work(job: BackupJob): Promise<BackupReply | null> {
    return new Promise((resolve) => {
      // A press's job that waited past the page's closing starts nothing.
      if (this.#stopped) {
        resolve(null);
        return;
      }
      let worker: BackupWorker;
      try {
        worker = this.#options.worker();
      } catch {
        resolve(null);
        return;
      }
      // One job at a time (T10′): the worker under way is this one until it ends.
      const end = (reply: BackupReply | null): void => {
        worker.terminate();
        this.#endWork = null;
        resolve(reply);
      };
      this.#endWork = end;
      worker.addEventListener('error', () => {
        end(null);
      });
      worker.addEventListener('messageerror', () => {
        end(null);
      });
      worker.addEventListener('message', (event) => {
        end(readReply(dataOf(event)));
      });
      worker.postMessage(job);
    });
  }

  /** The folder's handle and the records, read at start (N7: a stored handle that isn't one is no folder). */
  async #load(): Promise<void> {
    await this.#withLog(async (log) => {
      await this.#reloadFolder(log, true);
      await this.#refresh(log);
    });
  }

  /**
   * O2′ (deep-fix r2): the folder as the log holds it now, which another tab may have chosen or forgotten since; a read
   * that fails leaves it as it was. `starting`: a stored handle that isn't one is said (N7) as the app starts.
   */
  async #reloadFolder(log: BackupLog, starting: boolean): Promise<void> {
    if (this.#options.pickFolder === null) return;
    const read = await log.folder().then(
      (stored) => ({ stored }),
      () => null,
    );
    if (read === null) return;
    const { stored } = read;
    const before = this.#handle;
    if (isDirectory(stored)) {
      this.#handle = stored;
      this.#setFolder({ kind: 'set', name: stored.name });
      // O2′f: a notice about the folder this tab had says nothing true of another.
      if (before !== null && !(await stored.isSameEntry(before).catch(() => false))) this.#clearFolderNotice();
      return;
    }
    this.#handle = null;
    this.#setFolder({ kind: 'none' });
    if (before !== null) this.#clearFolderNotice();
    if (starting && stored !== undefined) this.#setNotice({ kind: 'lost' });
  }

  /** O2′f: puts away a notice about the folder (N1–N4), which another tab's change made untrue. */
  #clearFolderNotice(): void {
    const kind = this.notice()?.kind;
    if (kind === 'paused' || kind === 'refused' || kind === 'gone' || kind === 'full') this.#setNotice(null);
  }

  /** The meta record; one that can't be read is the defaults (a run is then due, and nothing put off). */
  async #meta(log: BackupLog): Promise<BackupMeta> {
    return log.meta().catch(() => NO_META);
  }

  /** The records and (part 41b) the lock read again; a log that can't be read leaves them as they were. */
  async #refresh(log: BackupLog): Promise<void> {
    try {
      this.#setRecords(await log.readAll());
    } catch {
      // Shown as before; the next read tries again.
    }
    await this.#readLock(log);
  }

  /** Runs `work` with the log, opened once and kept; one that can't be opened is said (N5) and opened again next time. */
  async #withLog(work: (log: BackupLog) => Promise<void>): Promise<void> {
    // r1-2: a log closed for a later build's upgrade is opened again on its next use.
    this.#log ??= this.#options.openLog(() => {
      this.#log = null;
    });
    let log: BackupLog;
    try {
      log = await this.#log;
    } catch {
      this.#log = null;
      this.#setNotice({ kind: 'failed' });
      return;
    }
    await work(log);
  }

  /** T2: the next check in `ms`. */
  #every(ms: number): void {
    this.#cancel = this.#options.schedule(ms, () => {
      this.#every(EVERY_MS);
      void this.check();
    });
  }

  /** The local day now. */
  #today(): string {
    const now = this.#options.now();
    return localDay(now, this.#options.zone(now));
  }

  /** Lets go of the reminder's file. */
  #forgetReminder(): void {
    const reminder = this.reminder();
    if (reminder !== null) this.#options.revokeUrl(reminder.url);
    this.#setReminder(null);
  }
}
